/**
 * Muestra versión de la app leída desde /version.json.
 * @module components/common/AppVersionDisplay
 */

import React from 'react';
import useAppVersionInfo from '../../hooks/useAppVersionInfo';
import { formatBuiltAt, formatShortCommit } from '../../services/appVersion.service';

const STATUS_LABEL = {
  updated: 'Actualizado',
  update_available: 'Nueva versión disponible',
  unknown: null
};

function StatusBadge({ status }) {
  const label = STATUS_LABEL[status];
  if (!label) return null;

  const isUpdate = status === 'update_available';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        isUpdate
          ? 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30'
          : 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${isUpdate ? 'bg-amber-400' : 'bg-emerald-400'}`}
        aria-hidden
      />
      {label}
    </span>
  );
}

/**
 * @param {{ variant?: 'sidebar'|'menu'|'footer'|'compact', className?: string, poll?: boolean }} props
 */
const AppVersionDisplay = ({ variant = 'sidebar', className = '', poll = true }) => {
  const { loading, remote, status, error } = useAppVersionInfo({ poll });

  if (error || (!loading && !remote)) {
    return null;
  }

  if (loading && !remote) {
    if (variant === 'compact') return null;
    return (
      <div className={`text-[10px] text-e0e3-muted/60 ${className}`} aria-hidden>
        Cargando versión…
      </div>
    );
  }

  const version = remote?.version || '—';
  const commit = formatShortCommit(remote?.commit);
  const builtAt = formatBuiltAt(remote?.builtAt);

  if (variant === 'compact') {
    return (
      <p
        className={`pointer-events-none select-none text-center font-mono text-[10px] text-e0e3-muted/60 ${className}`}
        title={`Build ${builtAt}`}
      >
        v{version} · {commit}
      </p>
    );
  }

  if (variant === 'menu') {
    return (
      <div className={`border-t border-white/[0.08] px-4 py-2 ${className}`}>
        <div className="text-[10px] font-mono text-e0e3-muted">
          v{version} · {commit}
        </div>
        <div className="mt-0.5 text-[10px] text-e0e3-muted/70">{builtAt}</div>
        <div className="mt-1.5">
          <StatusBadge status={status} />
        </div>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className={`mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] ${className}`}>
        <span className="font-mono text-e0e3-muted">
          v{version} · {commit}
        </span>
        <span className="text-e0e3-muted/70">{builtAt}</span>
        <StatusBadge status={status} />
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 text-center ${className}`}>
      <div className="font-mono text-[11px] text-e0e3-foreground">
        v{version} · {commit}
      </div>
      <div className="text-[10px] text-e0e3-muted/80">{builtAt}</div>
      <div className="flex justify-center">
        <StatusBadge status={status} />
      </div>
    </div>
  );
};

export default AppVersionDisplay;
